import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { OutlinedFlag as FlagIcon } from '@mui/icons-material';
import { useDeviceFlagsForDevice, useRecheckFlag } from '@/hooks/use-device-flags';
import { getAPIErrorMessage } from '@/lib/api';
import { checkSummary, TRIGGER_LABEL } from '@/lib/device-flag-format';
import { formatRelativeTime } from '@/lib/timezone';
import { SHELL_MONO } from '@/theme/tokens';

/**
 * Why this machine is flagged: one banner per enabled flag currently flagging
 * it, critical first (the API orders them). Super admins only.
 */
export function DeviceFlagBanners({ deviceId }: { deviceId: string }) {
  const { data: flags = [] } = useDeviceFlagsForDevice(deviceId, true);
  const recheck = useRecheckFlag();
  const [notice, setNotice] = useState<{ severity: 'success' | 'error'; text: string }>();

  if (!flags.length) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
      {notice && (
        <Alert severity={notice.severity} onClose={() => setNotice(undefined)}>
          {notice.text}
        </Alert>
      )}
      {flags.map((f) => (
        <Alert
          key={f.flag_id}
          severity={f.severity === 'critical' ? 'error' : 'warning'}
          icon={<FlagIcon fontSize="inherit" />}
          action={
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
              {f.check.kind === 'command' && (
                <Button
                  size="small"
                  color="inherit"
                  disabled={recheck.isPending}
                  onClick={() =>
                    recheck.mutate(
                      { id: f.flag_id, deviceId },
                      {
                        onSuccess: () => setNotice({ severity: 'success', text: `Re-checking "${f.name}".` }),
                        onError: (e) => setNotice({ severity: 'error', text: getAPIErrorMessage(e, 'Could not re-check') }),
                      },
                    )
                  }
                >
                  Re-check
                </Button>
              )}
              <Button size="small" color="inherit" component={RouterLink} to={`/device-flags/${f.flag_id}`}>
                View flag
              </Button>
            </Box>
          }
        >
          <AlertTitle sx={{ mb: 0.25 }}>{f.name}</AlertTitle>
          {f.message && <Typography sx={{ fontSize: 13 }}>{f.message}</Typography>}
          <Typography sx={{ fontSize: 12.5, mt: 0.75, opacity: 0.85 }}>
            Rule: <Box component="span" sx={{ fontFamily: SHELL_MONO }}>{checkSummary(f.check)}</Box>
            {' · read '}
            <Box component="span" sx={{ fontFamily: SHELL_MONO }}>{f.output.trim() || '(empty)'}</Box>
          </Typography>
          <Typography sx={{ fontSize: 12, mt: 0.25, opacity: 0.75 }}>
            Checked {formatRelativeTime(f.checked_at)} on {TRIGGER_LABEL[f.trigger].toLowerCase()}
            {f.flagged_since ? ` · flagged since ${formatRelativeTime(f.flagged_since)}` : ''}
            {f.error ? ` · last check failed: ${f.error}` : ''}
          </Typography>
        </Alert>
      ))}
    </Box>
  );
}
